/**
 * Boot: build the ws URL from the page origin, own the single NetClient,
 * and route server packets to the active phase (auth → hub → game). The
 * hub is the lobby (docs/design/hub.md): login lands you in the town — a
 * bare Hello, no room codes. The game owns the floor canvas + HUD; the hub
 * owns the town canvas + roster chrome; the auth screen owns sign-in/
 * creation (pre-game HTTP, a token in localStorage).
 *
 * A Welcome is the space-transition packet: roomId = HUB_ROOM_ID → the
 * hub scene, else a floor. Floor welcomes arrive on a socket-drop
 * reconnect (the same room's Welcome — the Game updates in place) and,
 * once the door lands (run.md's re-parent), on hub → floor transitions.
 * Hub welcomes arrive on login, on a hub socket-drop (the client's
 * stripped-Hello fallback in net.ts), and on floor → hub returns.
 */
import { ERR_ACCOUNT_IN_USE, ERR_AUTH_EXPIRED, ERR_NO_RECONNECT_SLOT, HUB_ROOM_ID } from "@pd/shared";
import type { Observation } from "@pd/shared";
import { NetClient } from "./net.js";
import type { SessionInfo } from "./net.js";
import { Hub } from "./hub.js";
import { Game } from "./game.js";
import { AuthScreen } from "./authScreen.js";
import { apiLogout, getAuthToken, getAuthUsername, setAuthToken } from "./auth.js";
import { installInputGuards } from "./input-guards.js";
import { loadHeroAssets, preloadGameArt } from "./hero.js";
import { loadSpriteStore } from "./spriteStore.js";
import { RecordsPanel } from "./recordsPanel.js";
import { installItemClickDebug } from "./item-click-debug.js";

installItemClickDebug();
const root = document.getElementById("app")!;
installInputGuards(root);
const recordsPanel = new RecordsPanel(root);
const wsUrl = `${location.protocol === "https:" ? "wss" : "ws"}://${location.host}/ws`;

let phase: "auth" | "hub" | "game" = "auth";
let auth: AuthScreen | null = null;
let hub: Hub | null = null;
let game: Game | null = null;

/** The hub re-parent Welcome queued behind a pending results screen
 *  (run.md's "After the screen"): on a floor end the wire order on this
 *  socket is Tick(floor-ended) THEN the hub Welcome (FIFO), but the results
 *  screen must be SEEN before the town — so the Welcome is held here and
 *  applied when the player dismisses the screen. */
let queuedHubWelcome: SessionInfo | null = null;

const net = new NetClient(wsUrl, {
  onWelcome: (info) => {
    if (info.roomId === HUB_ROOM_ID) {
      // A floor just ended on this socket (the Tick carrying floor-ended
      // landed a moment ago): hold the town re-parent until the results
      // screen is dismissed, then enter the hub with the queued info.
      if (phase === "game" && game && game.resultsPendingFlag) {
        queuedHubWelcome = info;
        game.onResultsDismissed = () => {
          const held = queuedHubWelcome;
          queuedHubWelcome = null;
          if (held) enterHub(held);
        };
        return;
      }
      enterHub(info);
      return;
    }
    enterFloor(info);
  },
  onRoomState: (p) => {
    if (phase === "game") game?.onRoomState(p);
  },
  onHubState: (p) => {
    if (phase === "hub") hub?.onHubState(p);
  },
  onHubChat: (p) => {
    if (phase === "hub") hub?.onHubChat(p);
  },
  // The race state goes to whichever scene is live (keyracing.md D2): the town
  // renders the consent window and the row, a floor renders the row alone. The
  // transport caches the packet as well, so a scene switch never blanks a race that
  // is still running — one side walking through a door does not pause the other's.
  onRaceState: (p) => {
    if (phase === "game") game?.onRaceState(p);
    else if (phase === "hub") hub?.onRaceState(p);
  },
  onRaceForfeit: (p) => {
    if (phase === "game") game?.onRaceForfeit(p);
    else if (phase === "hub") hub?.onRaceForfeit(p);
  },
  // A player card is a REPLY to one town menu (keyracing.md D1b), so the hub is
  // its only consumer: a floor never asked, and a card that arrives after the
  // town is gone has no window to fill.
  onPlayerCard: (p) => {
    if (phase === "hub") hub?.onPlayerCard(p);
  },
  onPlaytestLoadout: (p) => {
    if (phase === "hub") hub?.onPlaytestLoadout(p);
  },
  onRunRank: (p) => {
    // The rank lands right after the floor's re-parent Welcome — the client
    // holds that Welcome behind the results screen (above), so the phase is
    // still "game" and the screen that shows the rank is still alive.
    if (phase === "game") game?.onRunRank(p);
  },
  onTick: (p: { tick: number; observation: Observation; events: unknown[]; resynced?: boolean }) => {
    if (phase === "game") game?.onTick(p);
  },
  onRecovery: (state) => { if (phase === "game") game?.onRecovery(state); },
  onRecoveryEnded: (info, tick, rank) => {
    enterFloor(info);
    game?.onRecoveredFloor(tick);
    if (rank) game?.onRunRank(rank);
    if (game) game.onResultsDismissed = () => showAuth("The floor ended while you were away. Sign in to return to town.");
  },
  onError: (reason) => {
    if (reason === ERR_ACCOUNT_IN_USE) {
      // A second game tab must not erase the original tab's shared token.
      showAuth("This account is playing in another tab. Return to that tab to continue.");
      return;
    }
    // The server's word for a dead session token (the account's session
    // ended, the local db was reset, or the server restarted): drop the
    // credential and show the auth screen — a silent anonymous fallback would
    // drop the identity. net.ts has already stopped reconnecting (a dead
    // credential cannot be retried into life), so this fires ONCE.
    //
    // If the login is already on screen, DO NOT rebuild it: that erases
    // whatever the player has typed. Say why on the form that is there. (This
    // path used to run on every reconnect bounce, which is what made the login
    // screen appear to reset itself while typing.)
    if (reason === ERR_AUTH_EXPIRED) {
      setAuthToken(null);
      const why = "Your session ended. Sign in again.";
      if (phase === "auth" && auth) auth.showNotice(why);
      else showAuth(why);
      return;
    }
    if (reason === ERR_NO_RECONNECT_SLOT) {
      // A floor we were resuming is gone (it ended, or the server
      // restarted): the account session died with the dropped socket
      // (invalidate-on-disconnect), so the sign-in screen is the resting
      // state — not an anonymous town presence without a look or an account.
      setAuthToken(null);
      showAuth("That floor is gone. Sign in again.");
      return;
    }
    if (phase === "hub") hub?.onError(reason);
    else if (phase === "game") game?.onError(reason);
  },
  onClosed: () => {
    if (phase === "hub") {
      hub?.onClosed();
      // The server released the account's session with the socket
      // (invalidate-on-disconnect) and removed the town presence: a hub drop
      // re-logins (the sign-in screen, username pre-filled). A floor drop
      // does NOT come here — net.ts auto-resumes the frozen slot, and only
      // gives up (onError above) when the slot is gone too.
      showAuth("Connection lost. Sign in again.");
      return;
    }
    if (phase === "game" && game?.resultsPendingFlag && queuedHubWelcome) {
      // The server already moved this socket to town. Its town identity dies
      // on close even though the client is still showing floor results.
      queuedHubWelcome = null;
      game.onResultsDisconnected();
      game.onResultsDismissed = () => showAuth("Connection lost. Sign in to return to town.");
      return;
    }
    if (phase === "game") game?.onClosed();
  },
});

function onSignOut(): void {
  // The sign-in screen first: it flips the phase to "auth", so the
  // deliberate close below lands onClosed with nothing to do (no double
  // showAuth, no notice stomp).
  showAuth();
  net.close(); // deliberate: leave the town/floor, no auto-reconnect
  // Release the account's session server-side: the socket's close does the
  // same (invalidate-on-disconnect), but this is explicit and idempotent —
  // without a release the next sign-in is refused ("already signed in").
  const token = getAuthToken();
  if (token) void apiLogout(token);
  setAuthToken(null);
}

/** Enter (or re-enter) the town. A socket-drop reconnect in the hub re-
 *  enters this way: the scene keeps its canvas/assets and just re-anchors
 *  (new playerId — the hub's ids are monotonic, never reused). */
function enterHub(info: SessionInfo): void {
  queuedHubWelcome = null;
  if (game) {
    game.destroy();
    game = null;
  }
  phase = "hub";
  if (!hub) {
    root.textContent = "";
    hub = new Hub(root, net, { onSignOut });
  }
  hub.onWelcome(info);
}

/** Enter a floor: a fresh game screen (hub → door re-parent, the next
 *  slice) or the same room's Welcome after a socket drop (update in place
 *  — the viewport re-syncs on the next tick, like before the hub existed). */
function enterFloor(info: SessionInfo): void {
  if (phase === "game" && game) {
    game.onWelcome(info);
    return;
  }
  if (hub) {
    hub.destroy();
    hub = null;
  }
  phase = "game";
  root.textContent = "";
  game = new Game(root, net, info);
}

/** Build the login screen. Pass `notice` to explain why the player is back
 *  here; never call this to update an auth screen that is already showing —
 *  call `auth.showNotice` instead, or the player's half-typed credentials go
 *  with the rebuild. */
function showAuth(notice?: string): void {
  queuedHubWelcome = null;
  recordsPanel.close();
  phase = "auth";
  if (hub) {
    hub.destroy();
    hub = null;
  }
  if (game) {
    game.destroy();
    game = null;
  }
  root.textContent = "";
  auth = new AuthScreen(root, {
    onAuthed: () => {
      auth?.destroy();
      // A fresh session: a bare Hello — the server lands it in the hub.
      connectToHub();
    },
  });
  if (notice) auth.showNotice(notice);
}

/** The post-auth connect (login, refresh, re-login): a bare Hello with the
 *  account token. No roomId (there are no room codes any more), no seed
 *  (a client never chooses the seed of a floor it is about to run — the
 *  server picks it at the door, hub.md). */
function connectToHub(): void {
  net.connect(getAuthUsername() ?? "player", { auth: getAuthToken() ?? undefined });
}

// A token without a username (partial state) is unusable — drop it. A valid
// pair skips the auth screen and lands in the hub; the server re-validates
// the token on the Hello ("auth expired" bounces back through onError).
async function boot(): Promise<void> {
  const loading = document.createElement("div");
  loading.className = "art-loading";
  const title = document.createElement("h1");
  title.textContent = "Perfect Dungeon";
  const detail = document.createElement("p");
  detail.textContent = "Loading art…";
  loading.append(title, detail);
  root.replaceChildren(loading);
  try {
    await loadSpriteStore((stage) => { detail.textContent = stage; });
    const assets = await loadHeroAssets();
    if (!assets) throw new Error("sprite atlas could not be read");
    await preloadGameArt(assets, (done, total) => {
      if (done === total || done % 50 === 0) detail.textContent = `Preparing art… ${done} / ${total}`;
    });
    if (net.restoreFloor()) { /* A refresh resumes the tab's floor first. */ }
    else if (getAuthToken() && getAuthUsername()) connectToHub();
    else {
      setAuthToken(null);
      showAuth();
    }
  } catch (error) {
    detail.textContent = `Art could not be loaded: ${error instanceof Error ? error.message : String(error)}`;
    const retry = document.createElement("button");
    retry.textContent = "Retry";
    retry.addEventListener("click", () => location.reload());
    loading.append(retry);
  }
}

void boot();
